import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { get, isEqual } from 'lodash-es';
import Collapse from 'reactstrap/lib/Collapse';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { buildTaxesFeesPath, VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { getPrimaryIncentivesPath, IncentiveModel, IncentivesEntities } from 'client/data/models/incentives';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { formatPriceString, getPriceString } from 'site-modules/shared/utils/price-utils';
import { getIncentiveTitle } from 'site-modules/shared/utils/incentives';
import { PopoverItem } from 'site-modules/shared/components/popover-item/popover-item';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';
import { getOutOfDoorPrice } from 'site-modules/shared/utils/price-checker';
import { Link } from 'site-modules/shared/components/link/link';
import { StyledZipInput } from 'site-modules/shared/components/styled-zip-input/styled-zip-input';
import { PriceCheckerEntities } from 'site-modules/shared/constants/price-checker';
import { PriceCheckerPaymentCalculatorAsync } from 'site-modules/price-checker/components/price-checker-payment-calculator/price-checker-payment-calculator-async';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

import './price-checker-taxes-fees.scss';

const LIST_ITEM_CLASS_NAME = 'tax-fee-item d-flex justify-content-between';

const PRICE_BREAKDOWN_MAP = [
  {
    label: 'Taxes',
    items: [
      { name: 'State taxes', valuePath: 'taxes.amount.stateSalesTax' },
      { name: 'District taxes', valuePath: 'taxes.amount.districtSalesTax' },
      { name: 'County taxes', valuePath: 'taxes.amount.countySalesTax' },
      { name: 'City taxes', valuePath: 'taxes.amount.citySalesTax' },
      { name: 'Gas Guzzler taxes', valuePath: 'taxes.amount.gasGuzzlerTax' },
    ],
  },
  {
    label: 'Fees',
    items: [
      {
        name: 'Title fees',
        valuePath: 'fees.titleFee',
        tooltipText: 'The fee assessed when the title is transferred to the purchaser.',
      },
      {
        name: 'Registration fees',
        valuePath: 'fees.registrationFee',
        tooltipText:
          'The fee the state charges to register a vehicle, assign a title and cover the cost of license plates.',
      },
      { name: 'Miscellaneous fees', valuePath: 'fees.dmvFee', tooltipText: 'All other estimated government fees.' },
    ],
  },
];

const NOT_AVAILABLE = 'Not Available';
const UPDATING = 'Updating...';
const DASHES = '--';
const CREATIVE_ID = 'out_the_door_price';

export function fireTrackingZipChange(value) {
  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_COMPLETED,
    event_data: {
      action_name: TrackingConstant.ACTION_PROVIDE_ZIP_CODE,
      subaction_name: TrackingConstant.ACTION_PROVIDE_ZIP_CODE,
      action_category: TrackingConstant.USER_ACTION_CATEGORY,
      action_cause: TrackingConstant.ACTION_CAUSE_LINK_CLICK,
      creative_id: CREATIVE_ID,
      value,
    },
  });
}

function PriceCheckerTaxesFeesUI({
  showTaxesFees = false,
  isDealRated = false,
  taxesFees = null,
  primaryIncentives = null,
  tmvData = null,
  tmvAdjustment = null,
  userDealerPrice = null,
  userMsrp = null,
  bestDealPrice = null,
  incentivesLink = null,
  sharedDeal = null,
  calculatorProps = null,
  showCalculators = false,
  isMobile = false,
  mode,
  isCore6830International = false,
}) {
  const [isExpanded, setIsExpanded] = useState(false);
  const prevIsDealRated = usePrevious(isDealRated);
  const prevShowTaxesFees = usePrevious(showTaxesFees);

  if (prevIsDealRated !== isDealRated || prevShowTaxesFees !== showTaxesFees) {
    setIsExpanded(curr => isDealRated && (curr || showTaxesFees));
  }

  const { outOfDoorPrice, edmundsSuggestedPrice, outOfDoorTooltipText, descriptionText, isGoodPrice } =
    getOutOfDoorPrice({
      tmvData: { ...tmvData, ...sharedDeal },
      tmvAdjustment,
      bestDealPrice,
      userDealerPrice,
      userMsrp,
      totalTaxesFees: get(taxesFees, 'total', 0),
      isCore6830International,
    });
  const pricePlaceholder = taxesFees ? NOT_AVAILABLE : DASHES;
  const outTheDoorPlaceholder = taxesFees ? NOT_AVAILABLE : UPDATING;
  const calculatorCollapseBtnText = (
    <div>
      <i className="icon-calculator text-primary-darker" aria-hidden /> Calculate Monthly Payments
    </div>
  );
  const totalIncentives = get(primaryIncentives, 'length')
    ? primaryIncentives.reduce((total, { rebateAmount }) => total + rebateAmount, 0)
    : 0;

  return (
    <Collapse
      isOpen={isExpanded}
      aria-hidden={!isExpanded}
      className="price-checker-taxes-fees text-gray-darker pt-1 px-1 pb-md-1 bg-gray-extra-light medium mw-100 card"
      data-tracking-parent={CREATIVE_ID}
    >
      <ul className="list-unstyled mb-0_5">
        <li className={classnames('mb-1', LIST_ITEM_CLASS_NAME)}>
          <div>
            {isGoodPrice ? 'Dealer Price Quote:' : 'Edmunds Suggested Price:'}
            <div className="small text-gray-dark mt-0_25">After {formatPriceString(totalIncentives)} in rebates</div>
            {!!incentivesLink && (
              <Link to={incentivesLink} target="_blank" className="incentives-link disclaimer text-gray-dark">
                You may qualify for other rebates and incentives.
              </Link>
            )}
          </div>
          <div className="fw-bold">
            {getPriceString(isGoodPrice ? userDealerPrice : edmundsSuggestedPrice, pricePlaceholder)}
          </div>
        </li>
        {!isCore6830International && (
          <li className={classnames('mb-1', LIST_ITEM_CLASS_NAME)}>
            <div>Taxes and fees:</div>
            <div className="fw-bold">{getPriceString(get(taxesFees, 'total'), pricePlaceholder)}</div>
          </li>
        )}
      </ul>
      {!isCore6830International && (
        <ContentCollapse
          isEclipseFade={false}
          btnClasses="px-0_5 py-0 medium text-primary-darker"
          btnContainerClasses="mb-1"
          btnTextClasses="fw-normal"
          btnTrackingId="pc_taxes_fees"
          justify="justify-content-start"
          textClosed="Show pricing breakdown"
          textOpen="Hide pricing breakdown"
          hideCollapseBtn={!isExpanded}
        >
          {({ isOpen }) => (
            <div className="px-1">
              {!!get(primaryIncentives, 'length') && (
                <Fragment>
                  <div className="small fw-bold mb-0_25">Rebates</div>
                  <ul className="list-unstyled">
                    {primaryIncentives.map(incentive => {
                      const { id, rebateAmount, restrictions, details, endDate } = incentive;

                      return (
                        <li className={classnames('mb-0_5', LIST_ITEM_CLASS_NAME)} key={`${id}-${mode}`}>
                          <div className="pe-1_5">
                            {getIncentiveTitle(incentive)}{' '}
                            <div className="size-10 text-gray">
                              (exp. {endDate}){' '}
                              {isOpen && !!(details || restrictions) && (
                                <PopoverItem
                                  iconClassName="align-bottom text-gray-darker medium"
                                  screenReaderTooltipLabel="this incentive"
                                >
                                  Requirements and Restrictions:
                                  {!!restrictions && <div>{restrictions}</div>}
                                  {!!details && <div>{details}</div>}
                                </PopoverItem>
                              )}
                            </div>
                          </div>
                          <div>-{getPriceString(rebateAmount, NOT_AVAILABLE)}</div>
                        </li>
                      );
                    })}
                  </ul>
                </Fragment>
              )}
              {PRICE_BREAKDOWN_MAP.map(({ label, items }) => (
                <Fragment key={label}>
                  <div className="small fw-bold mb-0_25">{label}</div>
                  <ul className="list-unstyled mb-1">
                    {items.map(({ name, valuePath, tooltipText }) => (
                      <li className={classnames('mb-0_5', LIST_ITEM_CLASS_NAME)} key={`item-${name}`}>
                        <div>
                          {name}
                          {isOpen && !!tooltipText && (
                            <PopoverItem
                              placement="top"
                              iconClassName="text-gray-darker medium"
                              screenReaderTooltipLabel={name}
                            >
                              {tooltipText}
                            </PopoverItem>
                          )}
                        </div>
                        <div>{getPriceString(get(taxesFees, valuePath), pricePlaceholder)}</div>
                      </li>
                    ))}
                  </ul>
                </Fragment>
              ))}
            </div>
          )}
        </ContentCollapse>
      )}
      <hr className="mb-1 mt-0" />
      <div className="d-flex justify-content-between align-items-center mb-1_5">
        <div className="heading-5">
          Out-the-door price
          {!!outOfDoorTooltipText && (
            <PopoverItem
              placement="top"
              screenReaderTooltipLabel="Out-the-door price"
              iconClassName="text-gray-darker medium"
            >
              {outOfDoorTooltipText}
            </PopoverItem>
          )}
        </div>
        <div className={classnames({ 'heading-4': !isCore6830International, 'size-16': isCore6830International })}>
          {isCore6830International ? 'Enter zip code' : getPriceString(outOfDoorPrice, outTheDoorPlaceholder)}
        </div>
      </div>
      {isCore6830International ? (
        <StyledZipInput
          styledWrapperClasses="mb-0_5"
          wrapperClasses="zip-input-wrapper rounded-8"
          onValidate={validation.validateZip}
          fireTrackingZipChange={fireTrackingZipChange}
          placeholder="Enter ZIP for local taxes & fees"
          btnClassname="fw-medium btn-blue-50"
          btnTrackingId="provide_zip_code"
          inputClasses="medium rounded-8"
          errorTextClasses="pos-a"
          forceEmptyZip
          noZipFromStore
          withGoBtn
          noUpdateOnBlur
          withErrorText
        />
      ) : (
        <div className="description text-gray-dark small fst-italic mb-1">{descriptionText}</div>
      )}

      {showCalculators && (
        <div className="calculators">
          <hr className="mb-0 mt-1_5" />
          {!!outOfDoorPrice && (
            <ContentCollapse
              isEclipseFade={false}
              classes={classnames({ 'full-width': isMobile })}
              btnClasses="px-0 py-1 text-primary-darker w-100"
              btnContainerClasses="mb-1 px-0_5"
              btnTextClasses="mx-0 fw-normal text-decoration-none heading-6 d-flex justify-content-between align-items-center"
              btnTrackingId="pc_calculators"
              textClosed={
                <Fragment>
                  {calculatorCollapseBtnText}
                  <i className="icon-plus3 text-primary-darker" aria-hidden />
                </Fragment>
              }
              textOpen={
                <Fragment>
                  {calculatorCollapseBtnText}
                  <i className="icon-minus3 text-primary-darker" aria-hidden />
                </Fragment>
              }
              hideIndicator
              hideCollapseBtn={!isExpanded}
            >
              <PriceCheckerPaymentCalculatorAsync
                {...calculatorProps}
                userDealerPrice={userDealerPrice}
                userMsrp={userMsrp}
                edmundsSuggestedPrice={edmundsSuggestedPrice}
                isGoodPrice={isGoodPrice}
              />
            </ContentCollapse>
          )}
        </div>
      )}
    </Collapse>
  );
}

PriceCheckerTaxesFeesUI.propTypes = {
  styleId: PropTypes.number,
  vin: PropTypes.string,
  taxesFees: VehicleEntities.StyleTaxesFees,
  primaryIncentives: PropTypes.arrayOf(IncentivesEntities.IncentiveGraphql),
  tmvData: VehicleEntities.NewTmv,
  tmvAdjustment: VehicleEntities.NewTmvAdjustment,
  bestDealPrice: PropTypes.number,
  userDealerPrice: PropTypes.number,
  userMsrp: PropTypes.number,
  isDealRated: PropTypes.bool,
  calculatorProps: PropTypes.oneOfType([
    PropTypes.shape({
      vin: PropTypes.string,
    }),
    PropTypes.shape({
      style: PropTypes.shape({
        makeName: PropTypes.string,
        modelName: PropTypes.string,
        year: PropTypes.number,
        trimName: PropTypes.string,
        id: PropTypes.number,
        make: PropTypes.shape({ name: PropTypes.string }),
        model: PropTypes.shape({ name: PropTypes.string }),
        submodels: PropTypes.shape({ name: PropTypes.string, slug: PropTypes.string }),
      }),
      tmvData: VehicleEntities.NewTmv,
    }),
  ]),
  incentivesLink: PropTypes.string,
  showTaxesFees: PropTypes.bool,
  sharedDeal: PriceCheckerEntities.SharedDataEntity,
  showCalculators: PropTypes.bool,
  isMobile: PropTypes.bool,
  mode: PropTypes.string.isRequired,
  isCore6830International: PropTypes.bool,
};

export const PriceCheckerTaxesFees = connectToModel(React.memo(PriceCheckerTaxesFeesUI, isEqual), {
  taxesFees: bindToPath(
    ({ userMsrp, userDealerPrice, styleId, calculateTaxesFees, isCore6830International }) =>
      !isCore6830International &&
      calculateTaxesFees &&
      buildTaxesFeesPath({ msrp: userMsrp, dealerPrice: userDealerPrice, styleId }),
    VehicleModel
  ),
  primaryIncentives: bindToPath(({ styleId }) => getPrimaryIncentivesPath(styleId), IncentiveModel),
});
